// src/components/Protected.js
import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { signOut, getAuth } from "firebase/auth";
import "../firebase/app-auth";
import { useUserRole } from "../contexts/UserRoleContext";

/**
 * Protected
 * Props :
 * - permission?: string      → exige une permission
 * - allOf?: string[]         → exige TOUTES ces permissions
 * - anyOf?: string[]         → exige AU MOINS UNE de ces permissions
 * - children: ReactNode
 *
 * Priorité: allOf > anyOf > permission
 */
export default function Protected({ permission, allOf, anyOf, children }) {
  const {
    // états/auth
    authReady,
    loading,
    user,
    role,
    // statuts
    isDeleted,
    isLocked,
    isActive,
    isOwner,
    isSocieteLocked,
    paymentWarning,
    montantDu,
    totalAvances,
    soldeDu,
    // helpers
    can,
    getBlockMessage,
    refreshSession,
  } = useUserRole();

  const navigate = useNavigate();

  // Si le chargement dure plus de 5s, afficher le bouton "Rafraîchir la session"
  const [slowLoad, setSlowLoad] = useState(false);
  useEffect(() => {
    if (!authReady || loading) {
      // Après 5s : afficher le bouton manuel
      const t1 = setTimeout(() => setSlowLoad(true), 5000);
      // Après 8s : auto-réparation silencieuse (connexion/déconnexion rapide répétée)
      const t2 = setTimeout(() => {
        if (refreshSession) {
          refreshSession();
        }
      }, 8000);
      // Après 15s : rechargement complet de la page si toujours bloqué
      const t3 = setTimeout(() => {
        window.location.reload();
      }, 15000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
    setSlowLoad(false);
  }, [authReady, loading, refreshSession]);

  const handleLogout = async () => {
    try {
      await signOut(getAuth());
      navigate("/login");
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
  };

  // -------- Helpers permission sets ----------
  const hasAll = (perms) => Array.isArray(perms) && perms.every((p) => can(p));
  const hasAny = (perms) => Array.isArray(perms) && perms.some((p) => can(p));

  const isAllowed = () => {
    if (Array.isArray(allOf) && allOf.length > 0) return hasAll(allOf);
    if (Array.isArray(anyOf) && anyOf.length > 0) return hasAny(anyOf);
    if (typeof permission === "string" && permission.trim()) return can(permission.trim());
    return true; // aucune contrainte explicite
  };

  /* ================== GARDES D’ACCÈS ==================
     1) On attend que l’auth Firebase soit prête (authReady)
     2) On attend que le profil utilisateur (role) soit chargé
     3) On bloque si non connecté / supprimé / verrouillé / inactif
     4) On vérifie la/les permission(s) demandées
  ===================================================== */

  // 1) Auth pas prête → gate
  if (!authReady || loading) {
    return <FullScreenGate text="Initialisation de la session..." slowLoad={slowLoad} onRefresh={refreshSession} />;
  }

  // 2) Non connecté → renvoi login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 3) Profil pas encore lu (évite l’accès au Dashboard avant users/{uid})
  if (role === null || role === undefined) {
    return <FullScreenGate text="Chargement de votre profil..." />;
  }

  // 4) Comptes bloqués / inactifs / pharmacie verrouillée par admin
  if (isDeleted || (!isActive && !isOwner) || (isLocked && !isOwner) || isSocieteLocked) {
    return (
      <BlockedCard
        icon={isSocieteLocked ? "🔒" : isLocked ? "🔒" : !isActive ? "⏸️" : "🚫"}
        title={
          isSocieteLocked
            ? "Pharmacie verrouillée"
            : isLocked
            ? "Compte verrouillé"
            : !isActive
            ? "Compte désactivé"
            : "Accès refusé"
        }
        subtitle={
          isSocieteLocked
            ? "Votre pharmacie a été verrouillée par l'administrateur. Contactez le support pour plus d'informations."
            : getBlockMessage?.() ||
              (isLocked
                ? "Votre compte a été temporairement verrouillé."
                : !isActive
                ? "Votre compte a été désactivé par l'administrateur."
                : "Accès à l'application refusé.")
        }
        onLogout={handleLogout}
      />
    );
  }

  // 5) Permissions
  if (!isAllowed()) {
    return (
      <PermissionDeniedCard
        role={role}
        isOwner={isOwner}
        permission={permission}
        allOf={allOf}
        anyOf={anyOf}
        onBack={() => navigate(-1)}
        onDashboard={() => navigate("/dashboard")}
      />
    );
  }

  // 6) OK — afficher la bannière paymentWarning si présente
  const handleAcknowledge = async () => {
    try {
      const [{ doc, updateDoc, serverTimestamp }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('../firebase/config'),
      ]);
      await updateDoc(doc(db, "users", user.uid), {
        paymentWarning: null,
        paymentWarningReadAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("[Protected] Erreur acquittement avis:", e);
    }
  };

  return (
    <>
      {/* Bandeau affiché UNIQUEMENT quand l'admin envoie un avis de paiement */}
      {paymentWarning && (
        <div style={{
          background: soldeDu <= 0
            ? "linear-gradient(90deg,#064e3b,#065f46)"
            : "linear-gradient(90deg,#7c2d12,#b45309)",
          color: "#fff",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          fontSize: 14,
          fontWeight: 600,
          zIndex: 9999,
          position: "sticky",
          top: 0,
          boxShadow: "0 2px 10px rgba(0,0,0,.4)",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>⚠️ {paymentWarning}</span>
            {montantDu > 0 && (
              <span style={{
                fontSize: 13,
                fontWeight: 400,
                opacity: 0.9,
                borderLeft: "1px solid rgba(255,255,255,0.3)",
                paddingLeft: 10,
              }}>
                💰 Montant dû : <strong>{montantDu.toLocaleString("fr-MA")} MAD</strong>
                {" · "}Avances : <strong style={{ color: "#86efac" }}>{totalAvances.toLocaleString("fr-MA")} MAD</strong>
                {" · "}
                <strong style={{ color: soldeDu <= 0 ? "#6ee7b7" : "#fca5a5" }}>
                  {soldeDu <= 0 ? "✅ Soldé" : `Reste : ${soldeDu.toLocaleString("fr-MA")} MAD`}
                </strong>
              </span>
            )}
          </span>
          <button
            onClick={handleAcknowledge}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.5)",
              color: "#fff",
              borderRadius: 8,
              padding: "5px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            ✅ C'est compris
          </button>
        </div>
      )}
      {children}
    </>
  );
}

/* ================== UI Helpers ================== */

function FullScreenGate({ text = "Chargement...", slowLoad = false, onRefresh }) {
  return (
    <div
      style={{
        /* position:fixed → hors du flux, pas de CLS quand il disparaît */
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)",
        color: "#e1e6ef",
        padding: 24,
      }}
    >
      <div
        style={{
          background: "#0f1b33",
          border: "1px solid #2a3b55",
          borderRadius: 14,
          padding: "22px 26px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 10px 30px rgba(0,0,0,.35)",
          minWidth: 260,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 24,
              height: 24,
              border: "3px solid rgba(255,255,255,.25)",
              borderTop: "3px solid #7ee4e6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <div style={{ fontWeight: 700 }}>{text}</div>
        </div>

        {slowLoad && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: "100%" }}>
            <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
              La session prend plus de temps que prévu…
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => { if (onRefresh) onRefresh(); }}
                style={{
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ↻ Réinitialiser la session
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: "#94a3b8",
                  border: "1px solid #334155",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Recharger la page
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function BlockedCard({ icon = "🚫", title, subtitle, onLogout }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(120deg, #2d1b1b 0%, #3d2020 100%)",
        padding: 24,
        color: "#dc2626",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          width: "96vw",
          background: "#111f39",
          color: "#ffd1d1",
          border: "1px solid #dc2626",
          borderRadius: 14,
          padding: "22px 26px",
          boxShadow: "0 12px 38px rgba(0,0,0,.45)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 10 }}>{icon}</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{title}</div>
        <div style={{ lineHeight: 1.6, color: "#fca5a5", marginBottom: 16 }}>{subtitle}</div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={onLogout}
            style={{
              background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Se déconnecter
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Actualiser
          </button>
        </div>
      </div>
    </div>
  );
}

function PermissionDeniedCard({ role, isOwner, permission, allOf, anyOf, onBack, onDashboard }) {
  const buildMessage = () => {
    const map = {
      voir_achats: "Seul le pharmacien peut accéder aux achats",
      gerer_utilisateurs: "Seul le propriétaire, l'admin ou le pharmacien peut gérer les utilisateurs",
      parametres: "Seul le pharmacien peut accéder aux paramètres",
      voir_ventes: "Vous n'avez pas accès aux ventes",
      ajouter_stock: "Vous n'avez pas accès à la gestion du stock",
      modifier_roles: "Seul le propriétaire peut modifier les rôles",
    };
    if (Array.isArray(allOf) && allOf.length) {
      return `Permissions requises : toutes (${allOf.join(", ")})`;
    }
    if (Array.isArray(anyOf) && anyOf.length) {
      return `Permissions requises : au moins une parmi (${anyOf.join(", ")})`;
    }
    if (typeof permission === "string" && permission.trim()) {
      return map[permission] || "Permission insuffisante pour cette action";
    }
    return "Permission insuffisante pour cette action";
  };

  // Cas spécial gestion utilisateurs
  if (permission === "gerer_utilisateurs") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(120deg, #2d2416 0%, #3d3020 100%)",
          padding: 24,
          color: "#fbbf24",
        }}
      >
        <div
          style={{
            maxWidth: 640,
            width: "96vw",
            background: "#111f39",
            border: "1px solid rgba(251, 191, 36, 0.4)",
            borderRadius: 14,
            padding: "22px 26px",
            boxShadow: "0 12px 38px rgba(0,0,0,.45)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 10 }}>👑</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Accès restreint — Propriétaire uniquement
          </div>
          <div style={{ lineHeight: 1.6, color: "#fde68a", marginBottom: 14 }}>
            Seul le propriétaire, l'administrateur ou le pharmacien peut gérer les utilisateurs.
          </div>
          <div
            style={{
              fontSize: 14,
              color: "#eab308",
              marginBottom: 16,
              background: "rgba(0,0,0,0.35)",
              padding: "8px 16px",
              borderRadius: 999,
              display: "inline-block",
            }}
          >
            Votre rôle : <strong>{role}</strong> {isOwner ? "(👑 Propriétaire)" : ""}
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button
              onClick={onDashboard}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                padding: "10px 18px",
                fontSize: "16px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Retour au Dashboard
            </button>
            <button
              onClick={onBack}
              style={{
                background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                padding: "10px 18px",
                fontSize: "16px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(120deg, #2d2416 0%, #3d3020 100%)",
        padding: 24,
        color: "#f59e0b",
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: "96vw",
          background: "#111f39",
          border: "1px solid rgba(245, 158, 11, 0.4)",
          borderRadius: 14,
          padding: "22px 26px",
          boxShadow: "0 12px 38px rgba(0,0,0,.45)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 10 }}>⚠️</div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
          Permission insuffisante
        </div>
        <div style={{ lineHeight: 1.6, color: "#fcd34d", marginBottom: 12 }}>
          {buildMessage()}
        </div>
        <div
          style={{
            fontSize: 14,
            color: "#fcd34d",
            marginBottom: 16,
            background: "rgba(0,0,0,0.35)",
            padding: "8px 16px",
            borderRadius: 999,
            display: "inline-block",
          }}
        >
          Votre rôle : <strong>{role}</strong> {isOwner ? "(👑 Propriétaire)" : ""}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={onDashboard}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retour au Dashboard
          </button>
          <button
            onClick={onBack}
            style={{
              background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "10px 18px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Retour
          </button>
        </div>
      </div>
    </div>
  );
}
